import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import FlightSearchForm from './FlightSearchForm';
import FlightResults from './FlightResults';
import FlightFilters from './FlightFilters';
import AirlineFilterBar from './AirlineFilterBar';
import SortBar from './SortBar';
import DatePriceExplorer from './DatePriceExplorer';
import HotelSearchForm from './HotelSearchForm';
import HotelResults from './HotelResults';
import CarRentalSearchForm from './CarRentalSearchForm';
import CarRentalResults from './CarRentalResults';
import TripSummarySidebar from './TripSummarySidebar';
import TrialContextBanner from './TrialContextBanner';
import './TravelSearchPage.css';

/**
 * TravelSearchPage - Complete travel booking with smart auto-population
 * 
 * Features:
 * - Auto-populates hotel/car from flight search
 * - Warns user if they change dates/locations
 */
const TravelSearchPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('flights');

  // Trial context from NephroTrax
  const [trialContext, setTrialContext] = useState(null);

  // Flight search data (source of truth for auto-population)
  const [flightSearchData, setFlightSearchData] = useState(null);
  
  // Flight state
  const [outboundFlights, setOutboundFlights] = useState([]);
  const [returnFlights, setReturnFlights] = useState([]);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const [flightsError, setFlightsError] = useState(null);

  // Flight filters
  const [selectedStops, setSelectedStops] = useState([]);
  const [selectedAirlines, setSelectedAirlines] = useState([]);
  const [sortBy, setSortBy] = useState('best');

  // Trip selections (for summary sidebar)
  const [selectedFlights, setSelectedFlights] = useState({});
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);

  // Load trial context on mount
  useEffect(() => {
    console.log('🔍 TravelSearchPage: Checking for trial context in sessionStorage...');
    const storedContext = sessionStorage.getItem('ac_trial_context');
    console.log('📦 Raw sessionStorage value:', storedContext);
    
    if (storedContext) {
      try {
        const context = JSON.parse(storedContext);
        console.log('✅ Successfully parsed trial context:', context);
        setTrialContext(context);
      } catch (e) {
        console.error('❌ Failed to parse trial context:', e);
      }
    } else {
      console.log('⚠️ No trial context found in sessionStorage');
    }

    // RESTORE previous search state if returning from checkout
    const storedSearchState = sessionStorage.getItem('travel_search_state');
    if (storedSearchState) {
      try {
        const searchState = JSON.parse(storedSearchState);
        
        // Check if data is less than 1 hour old
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        if (searchState.timestamp && searchState.timestamp > oneHourAgo) {
          console.log('🔄 Restoring previous search state...');
          
          // Restore flight data
          if (searchState.flightSearchData) setFlightSearchData(searchState.flightSearchData);
          if (searchState.outboundFlights) setOutboundFlights(searchState.outboundFlights);
          if (searchState.returnFlights) setReturnFlights(searchState.returnFlights);
          if (searchState.selectedFlights) setSelectedFlights(searchState.selectedFlights);
          
          // Restore hotel data
          if (searchState.hotels) setHotels(searchState.hotels);
          if (searchState.selectedHotel) setSelectedHotel(searchState.selectedHotel);
          
          // Restore car data
          if (searchState.cars) setCars(searchState.cars);
          if (searchState.selectedCar) setSelectedCar(searchState.selectedCar);
          
          // Restore active tab
          if (searchState.activeTab) setActiveTab(searchState.activeTab);
          
          console.log('✅ Search state restored successfully');
        } else {
          console.log('⚠️ Search state is stale (>1 hour old), clearing...');
          sessionStorage.removeItem('travel_search_state');
        }
      } catch (e) {
        console.error('❌ Failed to restore search state:', e);
      }
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
  const params = new URLSearchParams(location.search);
  
  // Set active tab from URL
  const tab = params.get('tab');
  if (tab) setActiveTab(tab);
  
  // Store bookingId for saving selections later
  const bookingId = params.get('bookingId');
  if (bookingId) sessionStorage.setItem('current_booking_id', bookingId);

  // Pre-populate flight search fields via sessionStorage
  // (FlightSearchForm reads from trialContext — reuse that mechanism)
  const destination = params.get('destination');
  const departureDate = params.get('departureDate');
  const returnDate = params.get('returnDate');
  
  if (destination || departureDate) {
    const urlContext = {
      destination: destination,
      tripStartDate: departureDate,
      tripEndDate: returnDate,
      trialName: params.get('trialName'),
      trialId: params.get('trialId'), 
      fromBooking: true,
    };
    sessionStorage.setItem('ac_trial_context', JSON.stringify(urlContext));
    setTrialContext(urlContext);
  }

  // Pre-populate hotel
  const checkIn = params.get('checkIn');
  const checkOut = params.get('checkOut');
  const city = params.get('city');
  if (city || checkIn) {
    sessionStorage.setItem('hotel_prefill', JSON.stringify({ city, checkIn, checkOut }));
  }
}, [location.search]);

  // Hotel state
  const [hotels, setHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [hotelsError, setHotelsError] = useState(null);

  // Car rental state
  const [cars, setCars] = useState([]);
  const [carsLoading, setCarsLoading] = useState(false);
  const [carsError, setCarsError] = useState(null);

  // Flight search handler
  const handleFlightSearch = async (params) => {
    // Save flight search data for auto-population
    setFlightSearchData(params);
    
    setFlightsLoading(true);
    setFlightsError(null);
    setOutboundFlights([]);
    setReturnFlights([]);

    try {
      const response = await api.get('/v1/travel/flights/search', { params });

      if (response.data.success) {
        const flightData = response.data.data;
        setOutboundFlights(flightData?.outboundFlights || []);
        setReturnFlights(flightData?.returnFlights || []);
      } else {
        setFlightsError(response.data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Flight search error:', err);
      setFlightsError(err.response?.data?.error || 'Failed to search flights');
    } finally {
      setFlightsLoading(false);
    }
  };

  // Hotel search handler
  const handleHotelSearch = async (params) => {
    setHotelsLoading(true);
    setHotelsError(null);
    setHotels([]);

    try {
      const response = await api.get('/v1/travel/hotels/search', { params });

      console.log('Hotel search response:', response.data);

      if (response.data.success) {
        // Backend returns { success: true, data: { hotels: [...], totalResults: N } }
        const hotelData = response.data.data;
        setHotels(hotelData?.hotels || []);
      } else {
        setHotelsError(response.data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Hotel search error:', err);
      setHotelsError(err.response?.data?.error || 'Failed to search hotels');
    } finally {
      setHotelsLoading(false);
    }
  };

  // Car rental search handler
  const handleCarSearch = async (params) => {
    setCarsLoading(true);
    setCarsError(null);
    setCars([]);

    try {
      const response = await api.get('/v1/travel/cars/search', { params });

      console.log('Car search response:', response.data);

      if (response.data.success) {
        // Backend returns { success: true, data: { cars: [...], totalResults: N } }
        const carData = response.data.data;
        setCars(carData?.cars || carData || []);
      } else {
        setCarsError(response.data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Car search error:', err);
      setCarsError(err.response?.data?.error || 'Failed to search cars');
    } finally {
      setCarsLoading(false);
    }
  };

  // Selection handlers
  const handleSelectFlight = (flight, flightType) => {
    console.log(`${flightType} flight selected:`, flight);
    
    setSelectedFlights(prev => ({
      ...prev,
      [flightType.toLowerCase()]: flight
    }));
  };

  const handleSelectHotel = (hotel) => {
    console.log('Hotel selected:', hotel);
    setSelectedHotel(hotel);
  };

  const handleSelectCar = (car) => {
    console.log('Car selected:', car);
    setSelectedCar(car);
  };

  // Remove handlers
  const handleRemoveFlight = (flightType) => {
    setSelectedFlights(prev => {
      const updated = { ...prev };
      delete updated[flightType];
      return updated;
    });
  };

  const handleRemoveHotel = () => {
    setSelectedHotel(null);
  };

  const handleRemoveCar = () => {
    setSelectedCar(null);
  };

  // Checkout handler
const handleCheckout = async () => {
  const bookingId = sessionStorage.getItem('current_booking_id');

  const tripData = {
    outboundFlight: selectedFlights.outbound || null,
    returnFlight: selectedFlights.return || null,
    hotel: selectedHotel,
    car: selectedCar,
  };
  sessionStorage.setItem('trip_data', JSON.stringify(tripData));

  if (bookingId) {
    try {
      const updateData = {};
      if (selectedFlights.outbound) {
        updateData.flightBookingId = selectedFlights.outbound.id || selectedFlights.outbound.flightNumber;
        updateData.flightAirline = selectedFlights.outbound.airline;
        updateData.flightDepartureAirport = selectedFlights.outbound.origin;
        updateData.flightArrivalAirport = selectedFlights.outbound.destination;
        updateData.flightAmount = selectedFlights.outbound.price;
      }
      if (selectedHotel) {
        updateData.hotelBookingId = selectedHotel.id || selectedHotel.name;
        updateData.hotelName = selectedHotel.name;
        updateData.hotelAmount = selectedHotel.totalPrice || selectedHotel.pricePerNight;
      }
      if (selectedCar) {
        updateData.groundTransportType = selectedCar.carCategory;
        updateData.groundTransportAmount = selectedCar.totalPrice || selectedCar.pricePerDay;
      }

      await api.put(`/v1/travel/bookings/${bookingId}`, updateData);  // ← fixed
      sessionStorage.removeItem('current_booking_id');
      navigate(`/travel/bookings/${bookingId}`);
    } catch (err) {
      console.error('Failed to save booking selections:', err);
      alert('Failed to save selections: ' + (err.response?.data?.message || err.message));
    }
  } else {
    navigate('/checkout');
  }
};

  // Filter and sort flights
  const filterFlights = (flights) => {
    let filtered = flights;

    // Filter by stops
    if (selectedStops.length > 0) {
      filtered = filtered.filter(flight => selectedStops.includes(flight.stops || 0));
    }

    // Filter by airlines
    if (selectedAirlines.length > 0) {
      filtered = filtered.filter(flight => 
        selectedAirlines.includes(flight.airlineCode || flight.carrierCode)
      );
    }

    // Sort
    filtered = [...filtered]; // Create copy for sorting
    switch (sortBy) {
      case 'cheapest':
      case 'price':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'duration':
        filtered.sort((a, b) => {
          const getDuration = (flight) => {
            const match = flight.duration?.match(/PT(\d+)H(\d+)M/);
            if (!match) return 0;
            return parseInt(match[1]) * 60 + parseInt(match[2]);
          };
          return getDuration(a) - getDuration(b);
        });
        break;
      case 'stops':
        filtered.sort((a, b) => (a.stops || 0) - (b.stops || 0));
        break;
      case 'best':
      default:
        // Best = combination of price and stops
        filtered.sort((a, b) => {
          const scoreA = a.price + (a.stops || 0) * 50;
          const scoreB = b.price + (b.stops || 0) * 50;
          return scoreA - scoreB;
        });
        break;
    }

    return filtered;
  };

  const filteredOutboundFlights = filterFlights(outboundFlights);
  const filteredReturnFlights = filterFlights(returnFlights);

  return (
    <div className="travel-search-page">
      <div className="travel-search-container">
        <header className="travel-search-header">
          <h1>Book Your Travel</h1>
          <p>Search and book flights, hotels, and rental cars for your clinical trial visit</p>
        </header>

        {/* Trial Context Banner */}
        {trialContext && (
          <TrialContextBanner
            trialContext={trialContext}
            onDismiss={() => {
              setTrialContext(null);
              sessionStorage.removeItem('ac_trial_context');
            }}
          />
        )}

        {/* Tabs */}
        <div className="travel-tabs">
          <button
            className={`tab-button ${activeTab === 'flights' ? 'active' : ''}`}
            onClick={() => setActiveTab('flights')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
            </svg>
            Flights
          </button>

          <button
            className={`tab-button ${activeTab === 'hotels' ? 'active' : ''}`}
            onClick={() => setActiveTab('hotels')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Hotels
          </button>

          <button
            className={`tab-button ${activeTab === 'cars' ? 'active' : ''}`}
            onClick={() => setActiveTab('cars')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
            </svg>
            Rental Cars
          </button>
        </div>

        <div className="travel-search-content">
          {/* Main Content */}
          <div className="travel-main-content">
            {/* FLIGHTS TAB */}
            {activeTab === 'flights' && (
            <>
              <div className="search-form-section">
                <FlightSearchForm 
                  onSearch={handleFlightSearch}
                  loading={flightsLoading}
                  trialContext={trialContext}
                />
              </div>

              {(outboundFlights.length > 0 || returnFlights.length > 0) && (
                <div className="flight-results-container">
                  {/* Filters Sidebar */}
                  <div className="flight-filters-sidebar">
                    <FlightFilters
                      flights={[...outboundFlights, ...returnFlights]}
                      selectedStops={selectedStops}
                      selectedAirlines={selectedAirlines}
                      onStopsChange={setSelectedStops}
                      onAirlinesChange={setSelectedAirlines}
                    />
                  </div>

                  {/* Results */}
                  <div className="flight-results-main">
                    {/* Date Price Explorer */}
                    {flightSearchData?.departureDate && (
                      <DatePriceExplorer
                        selectedDate={flightSearchData.departureDate}
                        onDateSelect={(newDate) => {
                          // Re-search with new date
                          handleFlightSearch({
                            ...flightSearchData,
                            departureDate: newDate
                          });
                        }}
                      />
                    )}

                    {/* Airline Filter Pills */}
                    <AirlineFilterBar
                      flights={[...outboundFlights, ...returnFlights]}
                      selectedAirlines={selectedAirlines}
                      onAirlinesChange={setSelectedAirlines}
                    />

                    {filteredOutboundFlights.length > 0 && (
                      <div className="search-results-section">
                        {/* Sort Bar */}
                        <SortBar
                          totalResults={filteredOutboundFlights.length}
                          sortBy={sortBy}
                          onSortChange={setSortBy}
                        />

                        <FlightResults
                          flights={filteredOutboundFlights}
                          loading={flightsLoading}
                          flightType="Outbound"
                          error={flightsError}
                          onSelectFlight={(flight) => handleSelectFlight(flight, 'Outbound')}
                        />
                      </div>
                    )}

                    {filteredReturnFlights.length > 0 && (
                      <div className="search-results-section" style={{ marginTop: '1.5rem' }}>
                        <FlightResults
                          flights={filteredReturnFlights}
                          loading={flightsLoading}
                          flightType="Return"
                          error={flightsError}
                          onSelectFlight={(flight) => handleSelectFlight(flight, 'Return')}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* HOTELS TAB */}
          {activeTab === 'hotels' && (
            <>
              <div className="search-form-section">
                <HotelSearchForm 
                  onSearch={handleHotelSearch}
                  loading={hotelsLoading}
                  flightData={flightSearchData} // Pass flight data for auto-population
                />
              </div>

              {(hotelsLoading || hotels.length > 0 || hotelsError) && (
                <div className="search-results-section">
                  <HotelResults
                    hotels={hotels}
                    loading={hotelsLoading}
                    error={hotelsError}
                    onSelectHotel={handleSelectHotel}
                  />
                </div>
              )}
            </>
          )}

          {/* CARS TAB */}
          {activeTab === 'cars' && (
            <>
              <div className="search-form-section">
                <CarRentalSearchForm 
                  onSearch={handleCarSearch}
                  loading={carsLoading}
                  flightData={flightSearchData} // Pass flight data for auto-population
                />
              </div>

              {(carsLoading || cars.length > 0 || carsError) && (
                <div className="search-results-section">
                  <CarRentalResults
                    cars={cars}
                    loading={carsLoading}
                    error={carsError}
                    onSelectCar={handleSelectCar}
                  />
                </div>
              )}
            </>
          )}
          </div>

          {/* Trip Summary Sidebar */}
          <TripSummarySidebar
            selectedFlights={selectedFlights}
            selectedHotel={selectedHotel}
            selectedCar={selectedCar}
            onRemoveFlight={handleRemoveFlight}
            onRemoveHotel={handleRemoveHotel}
            onRemoveCar={handleRemoveCar}
            onCheckout={handleCheckout}
          />
        </div>
      </div>
    </div>
  );
};

export default TravelSearchPage;